import autoBind from 'auto-bind';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { getMeasureStartTick, roundMeasurePercent } from '../../../common/src/measure_percentages.ts';
import {
  BoundSongListEntry,
  Copyright,
  defaultVoiceNames,
  Hymn,
  hymnHasVocals,
  isMidiFileMissing,
  isSongFileMissing,
  SongFileExtension,
  SongList,
  SongTempos,
  SongTranspositions,
  sortVoices,
  vocalFileExtensions,
  Voice,
  voicesForVocalFiles,
  VoiceValues,
  YouTubeVideoId,
} from '../../../common/src/model.ts';
import { isBound } from '../../../common/src/model_util.ts';
import { hymnalsDir, orgHymnalDefaultIconPath } from '../../../common/src/paths.ts';
import { getDefaultTempo, SypMeasureNew, SypMusicFileKey, SypMusicFileNew } from '../../../common/src/syp_file.ts';
import { ensureExists, ensureUnreachable } from '../../../common/src/util.ts';
import {
  defaultVerseCount,
  getDefaultValue,
  localStorageGet,
  LocalStorageKey,
  RepeatMode,
  SongIntroduction,
} from '../data/client_local_storage.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { HymnalsContext, useDisplayHymnal } from '../data/use_hymnals.tsx';
import { useIsAutoPlayNextSongEnabled } from '../data/use_is_auto_play_next_song_enabled.ts';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { getCopyrightIssues } from '../util/copyright.ts';
import { NetworkError } from '../util/network.ts';
import {
  generateHymnUrlFromSlug,
  getHymnFromSlug,
  getMidiUrlForHymn,
  getPageFromLocation,
  isCustomSong,
} from '../util/path.ts';
import { Stopwatch } from '../util/stopwatch.ts';
import { useRetryOnReconnect } from '../util/use_retry_on_reconnect.tsx';
import { AudioPlayer } from './audio_player.ts';
import { CurrentSongContext, getMeasureFromPercent } from './current_song.tsx';
import { DualAudioElementInterface, useDualAudioElement } from './dual_audio_element.tsx';
import { MidiFilePlayer } from './midi_file_player.ts';
import { MidiFilePlayerWebAudio } from './midi_file_player_web_audio.ts';
import { registerPagePlayer } from './page_player.ts';
import {
  getIsPlaying,
  noteLoadFailed,
  noteSongTransition,
  setIsPlaying,
  subscribeIsPlaying,
} from './playback_state.ts';
import { notePracticeVideoData } from './practice_video/practice_video_channel.ts';
import { PracticeVideoContext } from './practice_video/practice_video_context.tsx';
import { loadYouTubeIframeApi, YouTubeNamespace } from './practice_video/youtube_iframe_api.ts';
import { YouTubeVideoPlayer } from './practice_video/youtube_video_player.ts';
import {
  isReloadDeferredByPlayback,
  isSongReloadHeld,
  noteSongReloadDeferred,
  shouldReloadForHashChange,
  SongFileVersion,
} from './song_reload.ts';
import { useSongReloadPulse } from './use_song_reload_pulse.ts';
import { useSongViewRecorder } from './use_song_view_recorder.ts';
import { VocalsPlayer } from './vocals_player.ts';

const minTempoValue = 5;
const maxTempoValue = 500;

export interface SequencerInterface {
  play: () => void;
  pause: () => void;
  previousSong: () => void;
  nextSong: () => void;
  setNewTime: (value: number) => void;
  changeVerse: (verseIndex: number) => void;
  get isPlaying(): boolean;
  playFirstNote: (voiceName: Voice) => void;
}

export interface SequencerResult {
  hymn: Hymn | undefined;
  copyrightIssues: Copyright[] | undefined;
  sypFile: SypMusicFileNew | undefined;
  sypFileParsed: boolean;
  getCurrentPercent: () => number | undefined;
  currentVerse: number;
  sequencer: SequencerInterface;
  duration: number | undefined;
  defaultTempo: number;
  currentTempo: number;
  clearPlaylist: () => void;
  setTempoCenterShift: (value: number | ((value: number) => number)) => void;
  setTempoMinorAdjustment: (value: number) => void;
  onTempoChange: () => void;
  clearUserSpecifiedTempo: () => void;
  registerClearTempoListener: (callback: () => void) => void;
  unregisterClearTempoListener: (callback: () => void) => void;
  networkError: NetworkError | undefined;
  nextSong: () => void;
  previousSong: () => void;
  openPlayer: ({
    playlist,
    initialSlug,
    playOnSongLoad,
    addHistoryEntry,
  }: {
    playlist: SongList;
    initialSlug?: string;
    playOnSongLoad?: boolean;
    // default true: Back closes the player (see openPlayer)
    addHistoryEntry?: boolean;
  }) => void;
  playing: boolean;
  playOnSongLoad: boolean;
  readyToPlay: boolean;
  stopwatch: Stopwatch;
  songContainsRepeats: boolean;
  setOverrideVoiceValuesWithDefaults: (value: boolean) => void;
  setPlayOnSongLoad: (value: boolean) => void;
  vocalsEnabled: boolean;
  // a YouTube practice video is the player's sound (GitHub issue #4041)
  videoEnabled: boolean;
  // the element the practice video's iframe lives in, for the video area to show
  practiceVideoHost: HTMLElement;
  hasVocals: boolean;
  hasMidi: boolean;
  voices: Voice[];
  // The player follows the tempo and volume sliders as they move (the Web Audio path); otherwise
  // a change is applied when the slider is let go
  liveControls: boolean;
  // A volume slider's value as it moves: heard at once, not stored. Storing the value when the
  // slider is let go (the voice values in local storage) replaces it.
  setLiveVoiceValue: (voice: Voice, value: number) => void;
}

export const SequencerContext = createContext<SequencerResult>({} as any);

export const SequencerProvider = ({ children }: { children: ReactNode }) => {
  const [playlist, setPlaylist] = useLocalStorage(LocalStorageKey.Playlist);
  const [playlistIndex, setPlaylistIndex] = useLocalStorage(LocalStorageKey.PlaylistIndex);
  const [isPlayerMaximized, setIsPlayerMaximized] = useLocalStorage(LocalStorageKey.IsPlayerMaximized);
  const {
    hymn,
    copyrightIssues,
    sypFile,
    sypFileParsed,
    networkError: hymnNetworkError,
    measureVisits,
    measurePercentagesWithRepeats,
    userKeyTransposition,
  } = use(CurrentSongContext);
  const { videoId: practiceVideoId, playbackRate: practiceVideoPlaybackRate } = use(PracticeVideoContext);
  const [sequencerNetworkError, setSequencerNetworkError] = useState<NetworkError | undefined>();
  const networkError = sequencerNetworkError ?? hymnNetworkError;
  // the sequencer writes the playback store; the retry below has to re-evaluate when it changes
  const isPlaying = useSyncExternalStore(subscribeIsPlaying, getIsPlaying);
  const dualAudioElement = useDualAudioElement();
  const { currentOrg, householdOrgs } = useCurrentOrg();
  const hymnals = use(HymnalsContext);
  const { customMusic } = useCustomMusic();
  const [songsForWeek] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const [orgHymnal] = useLocalStorage(LocalStorageKey.OrgHymnal);
  const { isInternalUser } = useUserAttributes();
  const [songIntroduction] = useLocalStorage(LocalStorageKey.SongIntroduction);
  const [voiceValues] = useLocalStorage(LocalStorageKey.VoiceValues);
  const [playing, setPlaying] = useState(false);
  const [readyToPlay, setReadyToPlay] = useState(false);
  const [currentVerse, setCurrentVerse] = useState(0); // 0 based verse index
  const [overrideVoiceValuesWithDefaults, setOverrideVoiceValuesWithDefaults] = useState(false);
  const voiceValuesOverride = useMemo(
    () => (overrideVoiceValuesWithDefaults ? getDefaultValue(LocalStorageKey.VoiceValues) : undefined),
    [overrideVoiceValuesWithDefaults],
  );
  const [playOnSongLoad, setPlayOnSongLoad] = useState(false);
  const [duration, setDuration] = useState<number | undefined>();
  const hasVocals =
    !!hymn &&
    hymnHasVocals(hymn) &&
    networkError !== NetworkError.MP3DownloadFailed &&
    networkError !== NetworkError.MP3GenerationFailed;
  const hasMidi =
    !!hymn &&
    !isMidiFileMissing(hymn) &&
    networkError !== NetworkError.MidiDownloadFailed &&
    networkError !== NetworkError.MidiFileParseFailed;
  const [showVocalsByDefault] = useLocalStorage(LocalStorageKey.ShowVocalsByDefault);
  const [webAudioPlayback] = useLocalStorage(LocalStorageKey.WebAudioPlayback);
  const [voices, setVoices] = useState<Voice[]>(defaultVoiceNames);
  const isAutoPlayNextSongEnabled = useIsAutoPlayNextSongEnabled();
  const [tempoCenterShift, setTempoCenterShift] = useState(() => 0);
  const [tempoMinorAdjustment, setTempoMinorAdjustment] = useState<number>(0);
  const [clearTempoListeners, setClearTempoListeners] = useState<(() => void)[]>([]);
  const onNetworkError = useCallback((networkError: NetworkError) => {
    // Logged, not recorded as an error (GitHub issue #3265): a song whose derived .syp/.mid files
    // have not been generated yet - or that is briefly missing while two servers are out of step -
    // 404s here, and that is expected. The UI degrades gracefully (the mixer and play button hide,
    // the PDF stays) and the next hash change reloads the song, so these must not reach
    // recordConsoleLog() as errors.
    console.log(`Network error: ${networkError}`);
    setSequencerNetworkError(networkError);
  }, []);

  // A fresh audio player load starts with a clean slate: without this a download failure stuck
  // around and kept the mixer and play button hidden until the playlist was reopened (item B6).
  const clearNetworkError = useCallback(() => {
    setSequencerNetworkError(undefined);
  }, []);

  const onVerseChange = useCallback((verse: number) => {
    setCurrentVerse(verse);
  }, []);

  const onDurationChange = useCallback((duration: number | undefined) => {
    setDuration(duration);
  }, []);

  const registerClearTempoListener = useCallback((callback: () => void) => {
    setClearTempoListeners((clearTempoListeners) => [...clearTempoListeners, callback]);
  }, []);

  const unregisterClearTempoListener = useCallback((callback: () => void) => {
    setClearTempoListeners((clearTempoListeners) => clearTempoListeners.filter((listener) => listener !== callback));
  }, []);

  // Whether the song may not be audibly played (music copyright without household access,
  // never for internal users); the sequencer uses this to skip such songs during auto-play
  // and to refuse to play them.
  const songHasMusicCopyrightIssue = useCallback(
    (songSlug: string) => {
      const hymn = getHymnFromSlug({ songSlug, hymnals, customMusic });
      const issues = getCopyrightIssues({ hymn, isInternalUser, songsForWeek, householdOrgs, hymnals, orgHymnal });
      return !!issues?.includes(Copyright.Music);
    },
    [customMusic, householdOrgs, hymnals, isInternalUser, orgHymnal, songsForWeek],
  );

  const clearUserSpecifiedTempo = useCallback(() => {
    setTempoCenterShift(0);
    setTempoMinorAdjustment(0);
    for (const clearTempoListener of clearTempoListeners) {
      clearTempoListener();
    }
  }, [clearTempoListeners]);

  const sequencer: Sequencer = useMemo(
    () =>
      new Sequencer({
        dualAudioElement,
        setReadyToPlay,
        onNetworkError,
        clearNetworkError,
        setPlaying,
        onVerseChange,
        setPlaylistIndex,
        setPlayOnSongLoad,
        onDurationChange,
      }),
    // eslint-disable-next-line
    [],
  );

  // for code outside the Player's React tree (page_player.ts)
  useEffect(() => registerPagePlayer(sequencer), [sequencer]);
  // Retry a failed load when the situation plausibly changed - the device came back online or the
  // user came back to the app - rather than leaving the song stuck on its error until it is
  // reloaded by hand (issue #3517 item B6). A hash change retries via setCurrentSong below.
  // Subscribing to the playback store (rather than reading it once) is what arms the listeners
  // the moment playback stops: reloading the current song while it plays is not allowed.
  const retryLoad = useCallback(() => sequencer.reloadCurrentSong(), [sequencer]);
  // no retry for a file the manifest says does not exist: only its hash change can bring it (a practice
  // video needs no file of the song's, so its failure is retried), nor for a video YouTube will not play at all
  useRetryOnReconnect(
    !!sequencerNetworkError &&
      sequencerNetworkError !== NetworkError.VideoUnavailable &&
      !isPlaying &&
      !(hymn && isSongFileMissing(hymn, SongFileExtension.Mid) && !practiceVideoId),
    retryLoad,
  );

  const [repeatMode] = useLocalStorage(LocalStorageKey.Repeat);

  useEffect(() => {
    sequencer.setRepeatMode(repeatMode);
  }, [repeatMode, sequencer]);

  useEffect(() => {
    sequencer.setIsAutoPlayNextSongEnabled(isAutoPlayNextSongEnabled);
  }, [isAutoPlayNextSongEnabled, sequencer]);

  useEffect(() => {
    if (songIntroduction) {
    }
    sequencer?.reset();
  }, [sequencer, songIntroduction]);

  // TODO(hewitt): Sequencer will have direct access to these when we combine the class into a global context
  sequencer.setOrgTranspositions(currentOrg?.songTranspositions ?? {});
  sequencer.setUserKeyTransposition(userKeyTransposition);
  sequencer.setUserSpecifiedTempo(tempoCenterShift, tempoMinorAdjustment);
  sequencer.setShowVocalsByDefault(showVocalsByDefault);
  sequencer.setWebAudioPlayback(webAudioPlayback);
  sequencer.setOrgTempos(currentOrg?.songTempos ?? {});
  sequencer.setUserVoiceValues(voiceValues);
  sequencer.setVoiceValuesOverride(voiceValuesOverride);
  sequencer.setSongIntroduction(songIntroduction);
  sequencer.setMeasurePercentagesWithRepeats(measurePercentagesWithRepeats);
  sequencer.setMeasureVisitMeasures(measureVisits.map(({ measure }) => measure));
  sequencer.setClearUserSpecifiedTempo(clearUserSpecifiedTempo);
  sequencer.setSongHasMusicCopyrightIssue(songHasMusicCopyrightIssue);
  const { defaultTempo, currentTempo, onTempoChange } = sequencer;
  // From the practice video context rather than the sequencer's getters, which repeat this rule: the sequencer
  // hears of a new video only in the setCurrentSong effect below, after this render, since taking it rebuilds the
  // audio player
  const videoEnabled = !!practiceVideoId;
  const vocalsEnabled = !videoEnabled && sequencer.songVocalsEnabled;

  // YouTube's terms: the picture stays in view while a practice video plays, so minimizing the player pauses it
  useEffect(() => {
    if (!isPlayerMaximized && practiceVideoId) {
      sequencer.pause();
    }
  }, [isPlayerMaximized, practiceVideoId, sequencer]);

  // A player that applies changes live follows the tempo as the slider moves: the slider changes
  // the tempo state, the render above hands it to the sequencer, and this tells the player. The
  // slider's own call when it is let go remains for the players told once.
  useEffect(() => {
    if (sequencer.appliesChangesLive) {
      sequencer.onTempoChange();
    }
  }, [currentTempo, sequencer]);

  const songContainsRepeats = useMemo(() => !!sypFile && !!sypFile.measures.find(({ goto }) => goto), [sypFile]);

  // the title the org selected for the hymn's hymnal, e.g. an alternate title (issue #3454)
  const displayHymnal = useDisplayHymnal(hymn);
  useEffect(() => {
    // Register for the iPhone lock screen
    // See this tutorial: https://web.dev/media-session/
    if ('mediaSession' in navigator && hymn && playing && playlist) {
      const src = isCustomSong(hymn.slug)
        ? orgHymnalDefaultIconPath
        : (displayHymnal?.icon?.path ?? `/${hymnalsDir}/${hymn.hymnal}/icon.png`);
      navigator.mediaSession.metadata = new MediaMetadata({
        title: hymn.title,
        artist: hymn.author ?? hymn.composer ?? '',
        album: displayHymnal?.name ?? hymn.hymnal,
        artwork: [{ src, sizes: '128x128', type: 'image/png' }],
      });
      navigator.mediaSession.setActionHandler('play', sequencer.play);
      navigator.mediaSession.setActionHandler('pause', sequencer.pause);
      if (playlist.songs.length > 1) {
        navigator.mediaSession.setActionHandler('previoustrack', () => sequencer.previousSong());
        navigator.mediaSession.setActionHandler('nexttrack', () => sequencer.nextSong());
      }
    }
  }, [displayHymnal, hymn, playing, playlist, sequencer]);

  const onVoiceChange = useCallback(
    (voices: Voice[]) => {
      voices = sortVoices(voices);
      if (voices.length === 0) {
        const filteredVoiceNames = voicesForVocalFiles
          .map((voice) =>
            getVoiceName({
              voice,
              sypFile,
              vocalPartCount: hymn?.vocalPartCount,
              vocalsEnabled,
            }),
          )
          .filter((voiceName) => voiceName !== null);
        setVoices(filteredVoiceNames);
        return;
      }
      setVoices(voices);
    },
    [hymn?.vocalPartCount, sypFile, vocalsEnabled],
  );

  useEffect(() => {
    // This is only necessary because of the call to getVoiceName for temporary backward compatibility
    // Once that is eliminated, this could be folded into the MidiFilePlayer constructor
    sequencer.setOnVoiceChange(onVoiceChange);
  }, [onVoiceChange, sequencer]);

  // A hash change while the song is playing leaves the loaded MIDI / vocals alone. The pulse
  // advances when the update gate opens, re-running this effect so setCurrentSong can rebuild the
  // audio player on the refreshed files (issue #3265).
  const songReloadPulse = useSongReloadPulse();

  // the practice video's speed, before the effect below, so a video player it builds starts at that speed; in an
  // effect of its own, so moving the speed slider does not hand the song to the sequencer again
  useEffect(() => {
    sequencer.setPracticeVideoPlaybackRate(practiceVideoPlaybackRate);
  }, [practiceVideoPlaybackRate, sequencer]);

  // TODO(hewitt): the playlist index can change independently of the hymn, triggering refreshes
  // TODO(hewitt): the hymn flops back and forth, depending on whether the .syp file is parsed
  // TODO(hewitt): instead, we should cache the parsed syp file in a lookup and only set hymn when we have it
  // The practice video goes in with the song: a song change into or out of video mode builds one player, for the
  // new song's sound, rather than one for the old pair of song and video first (GitHub issue #4041)
  useEffect(() => {
    sequencer.setCurrentSong({ playlist, playlistIndex, hymn, playSongOnce: songContainsRepeats, practiceVideoId });
  }, [playlist, playlistIndex, hymn, songContainsRepeats, sequencer, songReloadPulse, practiceVideoId]);

  // Question: Is this the right location for this function?
  const openPlayer = useCallback(
    ({
      playlist,
      initialSlug,
      playOnSongLoad,
      addHistoryEntry = true,
    }: {
      playlist: SongList;
      initialSlug?: string;
      playOnSongLoad?: boolean;
      addHistoryEntry?: boolean;
    }) => {
      if (playlist.songs.length === 0) {
        return;
      }
      const boundSongs = playlist.songs.filter((entry): entry is BoundSongListEntry => !!(entry as any).slug);
      const initialIndexInBound = initialSlug ? boundSongs.findIndex(({ slug }) => slug === initialSlug) : 0;
      const resolvedIndex = initialIndexInBound >= 0 ? initialIndexInBound : 0;
      sequencer?.reset();
      setPlaylist(playlist);
      setPlaylistIndex(resolvedIndex);
      setSequencerNetworkError(undefined);
      clearUserSpecifiedTempo();
      if (playOnSongLoad) {
        setPlayOnSongLoad(true);
      }
      setIsPlayerMaximized(true);
      const page = getPageFromLocation(window.location);
      const songSlug = boundSongs[resolvedIndex]?.slug ?? (playlist.songs[0] as BoundSongListEntry).slug;
      const hymnUrl = generateHymnUrlFromSlug({ songSlug, page });
      // The entry lets Back close the player. A caller that manages its own history opts out:
      // the Family Worship tutorial's extra entry sat between the tutorial and the tutorials
      // list, so Back from the list restarted the tutorial (GitHub issue #3551).
      if (addHistoryEntry) {
        history.pushState(history.state, document.title, hymnUrl);
      }
    },
    [clearUserSpecifiedTempo, sequencer, setIsPlayerMaximized, setPlaylist, setPlaylistIndex],
  );

  const clearPlaylist = useCallback(() => {
    setPlaylist(undefined);
    setIsPlayerMaximized(false);
  }, [setIsPlayerMaximized, setPlaylist]);

  useSongViewRecorder({ hymn, stopwatch: sequencer.stopwatch });

  return (
    <SequencerContext
      value={{
        hymn,
        copyrightIssues,
        duration,
        sypFile,
        sypFileParsed,
        getCurrentPercent: sequencer.getCurrentPercent,
        currentVerse,
        clearPlaylist,
        sequencer,
        defaultTempo,
        currentTempo,
        setTempoMinorAdjustment,
        setTempoCenterShift,
        onTempoChange,
        clearUserSpecifiedTempo,
        registerClearTempoListener,
        unregisterClearTempoListener,
        networkError,
        nextSong: sequencer.nextSong,
        previousSong: sequencer.previousSong,
        openPlayer,
        playing,
        playOnSongLoad,
        readyToPlay,
        stopwatch: sequencer.stopwatch,
        songContainsRepeats,
        setOverrideVoiceValuesWithDefaults,
        setPlayOnSongLoad,
        vocalsEnabled,
        videoEnabled,
        practiceVideoHost: sequencer.practiceVideoHost,
        hasVocals,
        hasMidi,
        voices: voices,
        liveControls: sequencer.appliesChangesLive,
        setLiveVoiceValue: sequencer.setLiveVoiceValue,
      }}
    >
      {children}
    </SequencerContext>
  );
};

// exported for unit tests - the app builds exactly one, in SequencerProvider above
export class Sequencer implements SequencerInterface {
  private readonly dualAudioElement: DualAudioElementInterface;
  private songIntroduction?: SongIntroduction;
  private measurePercentagesWithRepeats: number[] | undefined;
  private measureVisitMeasures: SypMeasureNew[] = [];
  private _verseIndex = 0; // todo: state change event to UI?
  private _playlist: SongList | undefined;
  private _playlistIndex = 0; // use setPlaylistIndex to update!

  // Playback state lives in the React-free playback_state store, so code above the sequencer's
  // context (the notation fetch, the PDF load) can tell whether reloading files is safe, and the
  // provider can subscribe to it (issue #3517). The sequencer is its only writer.
  private get _playing(): boolean {
    return getIsPlaying();
  }

  private set _playing(value: boolean) {
    setIsPlaying(value);
  }

  private _stopwatchSubscription: number | undefined;
  private _audioPlayer?: AudioPlayer;
  private _hymn?: Hymn;
  // the slug and content hash the current audio player was built from - what a later hash change
  // is compared against, so a reload deferred while playing is still owed when playback stops
  private _loadedSongVersion: SongFileVersion | undefined;
  // the song whose MIDI the manifest said does not exist, so no player was built for it: without
  // this, every later setCurrentSong for the same song would rebuild and report the error again
  private _midiMissingForSlug: string | undefined;
  private readonly _setReadyToPlay: (value: boolean) => void;
  private readonly _onNetworkError?: (networkError: NetworkError) => void;
  private readonly _setPlaying: (playing: boolean) => void;
  private readonly _clearNetworkError: () => void;
  private readonly _onVerseChange?: (verse: number) => void;
  private onVoiceChange?: (voiceNames: Voice[]) => void;
  private readonly onDurationChange?: (duration: number | undefined) => void;
  private readonly setPlaylistIndex?: (index: number) => void;
  private showVocalsByDefault: boolean;
  // the YouTube practice video playing in place of the piano or vocals (GitHub issue #4041)
  private practiceVideoId: YouTubeVideoId | undefined;
  private practiceVideoPlaybackRate = 1;
  private readonly loadYouTubeApi: () => Promise<YouTubeNamespace>;
  private _practiceVideoHost: HTMLDivElement | undefined;
  // The "Web Audio" song option (issue #3718): MIDI songs scheduled on the page's AudioContext
  // rather than rendered to a WAV for media elements
  private webAudioPlayback = false;
  private tempoMinorAdjustment = 0;
  private tempoCenterShift = 0;
  private clearUserSpecifiedTempo: (() => void) | undefined;
  private orgTempos: SongTempos = {};
  private orgTranspositions: SongTranspositions = {};
  private userKeyTransposition = 0;
  private _updateLocationSlider?: (currentPercent: number) => void;
  private voiceValuesOverride: VoiceValues | undefined;
  private userVoiceValues: VoiceValues = {};
  private liveVoiceValues: VoiceValues = {}; // the sliders as they move, over the stored values
  private playSongOnce?: boolean;
  private playOnSongLoad: boolean | undefined;
  private readonly setPlayOnSongLoad: (value: boolean) => void;
  private isAutoPlayNextSongEnabled = false;
  private repeatMode: RepeatMode = RepeatMode.Off;
  private songHasMusicCopyrightIssue: (songSlug: string) => boolean = () => false;
  stopwatch = new Stopwatch();

  constructor({
    dualAudioElement,
    onNetworkError,
    clearNetworkError,
    setReadyToPlay,
    onVerseChange,
    onDurationChange,
    setPlaying,
    setPlaylistIndex,
    setPlayOnSongLoad,
    loadYouTubeApi = loadYouTubeIframeApi,
  }: {
    dualAudioElement: DualAudioElementInterface;
    setReadyToPlay: (value: boolean) => void;
    onNetworkError: (networkError: NetworkError) => void;
    clearNetworkError: () => void;
    setPlaying: (playing: boolean) => void;
    onVerseChange: (verse: number) => void;
    onDurationChange: (duration: number | undefined) => void;
    setPlaylistIndex: (index: number) => void;
    setPlayOnSongLoad: (value: boolean) => void;
    // the YouTube IFrame API; a test hands in a fake
    loadYouTubeApi?: () => Promise<YouTubeNamespace>;
  }) {
    autoBind(this);
    this.dualAudioElement = dualAudioElement;
    this._setPlaying = setPlaying;
    this._setReadyToPlay = setReadyToPlay;
    this._onNetworkError = onNetworkError;
    this._clearNetworkError = clearNetworkError;
    this._onVerseChange = onVerseChange;
    this.onDurationChange = onDurationChange;
    this.setPlaylistIndex = setPlaylistIndex;
    this.setPlayOnSongLoad = setPlayOnSongLoad;
    this.songIntroduction = localStorageGet(LocalStorageKey.SongIntroduction);
    this.showVocalsByDefault = false;
    this.loadYouTubeApi = loadYouTubeApi;
  }

  setUserVoiceValues(voiceValues: VoiceValues) {
    // stored values are the sliders at rest: a live value is dropped even when the stored value
    // did not change (a slider moved and put back), or it would mask a later override
    const hadLiveValues = this.dropLiveVoiceValues();
    if (!hadLiveValues && JSON.stringify(voiceValues) === JSON.stringify(this.userVoiceValues)) {
      return;
    }
    this.userVoiceValues = voiceValues;
    this.onVolumeChange();
  }

  private dropLiveVoiceValues(): boolean {
    const hadLiveValues = Object.keys(this.liveVoiceValues).length > 0;
    this.liveVoiceValues = {};
    return hadLiveValues;
  }

  // Whether the player follows a slider as it moves (see AudioPlayer.appliesChangesLive)
  get appliesChangesLive(): boolean {
    return !!this._audioPlayer?.appliesChangesLive;
  }

  // Only a player that applies changes live is told; the others hear the value when it is stored
  setLiveVoiceValue(voice: Voice, value: number) {
    if (!this.appliesChangesLive || this.liveVoiceValues[voice] === value) {
      return;
    }
    this.liveVoiceValues[voice] = value;
    this.onVolumeChange();
  }

  setVoiceValuesOverride(voiceValuesOverride: VoiceValues | undefined) {
    const hadLiveValues = this.dropLiveVoiceValues();
    if (!hadLiveValues && JSON.stringify(voiceValuesOverride) === JSON.stringify(this.voiceValuesOverride)) {
      return;
    }
    this.voiceValuesOverride = voiceValuesOverride;
    this.onVolumeChange();
  }

  setSongIntroduction(songIntroduction: SongIntroduction | undefined) {
    if (this.songIntroduction === songIntroduction) {
      return;
    }
    this.songIntroduction = songIntroduction;
    this.refreshSongIntroduction();
  }

  setMeasurePercentagesWithRepeats(measurePercentagesWithRepeats: number[] | undefined) {
    if (JSON.stringify(this.measurePercentagesWithRepeats) === JSON.stringify(measurePercentagesWithRepeats)) {
      return;
    }
    this.measurePercentagesWithRepeats = measurePercentagesWithRepeats;
    this.refreshSongIntroduction();
  }

  setMeasureVisitMeasures(measures: SypMeasureNew[]) {
    this.measureVisitMeasures = measures;
  }

  get currentTempo(): number {
    return Math.max(
      minTempoValue,
      Math.min(
        maxTempoValue,
        Math.round(
          this.defaultTempo + this.tempoCenterShift + this.tempoMinorAdjustment * Math.abs(this.tempoMinorAdjustment),
        ),
      ),
    );
  }

  setClearUserSpecifiedTempo(clearUserSpecifiedTempo: () => void) {
    this.clearUserSpecifiedTempo = clearUserSpecifiedTempo;
  }

  setOnVoiceChange(onVoiceChange: (voices: Voice[]) => void) {
    this.onVoiceChange = onVoiceChange;
  }

  setIsAutoPlayNextSongEnabled(value: boolean) {
    this.isAutoPlayNextSongEnabled = value;
  }

  setRepeatMode(value: RepeatMode) {
    this.repeatMode = value;
  }

  setSongHasMusicCopyrightIssue(value: (songSlug: string) => boolean) {
    this.songHasMusicCopyrightIssue = value;
  }

  private get currentSongHasMusicCopyrightIssue(): boolean {
    return !!this._hymn?.slug && this.songHasMusicCopyrightIssue(this._hymn.slug);
  }

  updateLocationSlider() {
    this._updateLocationSlider?.(this.getCurrentPercent() ?? 0);
  }

  destroy() {
    this._pause();
    this._audioPlayer?.destroy();
  }

  setCurrentSong({
    hymn,
    playlist,
    playlistIndex,
    playSongOnce,
    practiceVideoId,
  }: {
    hymn: Hymn | undefined;
    playlist: SongList | undefined;
    playlistIndex: number;
    playSongOnce?: boolean;
    // the song's practice video playing in place of its piano or vocals (GitHub issue #4041)
    practiceVideoId?: YouTubeVideoId;
  }) {
    // A song whose files changed in the cache keeps the same slug and the same urls, so the hash
    // is the only signal that the loaded MIDI / vocals are stale. Rebuild for it exactly as for a
    // new song, but only while the playback gate is open - a hash change must never interrupt
    // playback (#3517 B1), nor a pause between verses (#3265).
    // The comparison is against the version the audio player was BUILT from, not against the hymn
    // this sequencer last heard about: a hash change deferred while playing must still read as a
    // change when the update gate re-runs this later (issue #3265).
    const versions = {
      previous: this._loadedSongVersion,
      next: hymn && { slug: hymn.slug, hash: hymn.hash },
      isHeld: isSongReloadHeld(),
    };
    const reloadAction = shouldReloadForHashChange(versions);
    const songRefreshed = reloadAction !== 'none';
    const rebuildsPlayer = (!this._audioPlayer && !(hymn && this._midiMissingForSlug === hymn.slug)) || songRefreshed;
    this._playlist = playlist;
    this._playlistIndex = playlistIndex;
    this._hymn = hymn;
    this.playSongOnce = playSongOnce;
    if (rebuildsPlayer) {
      this.practiceVideoId = practiceVideoId;
      this._audioPlayer = this.createAudioPlayer();
    } else {
      this.setPracticeVideo(practiceVideoId);
    }
    this.setLoop();
    if (reloadAction === 'new-song') {
      // A different song is loaded (or the first one). Raised last, once this sequencer is
      // consistent, because the update gate may apply a pending update - up to reloading the
      // page - the moment it hears this (issue #3265). A same-song file refresh is deliberately
      // not a transition: it is still the song the user has been singing.
      noteSongTransition();
    } else if (!rebuildsPlayer && isReloadDeferredByPlayback(versions)) {
      // The files changed under a playing song, and the player kept the old ones: ask the update
      // gate to tell every loader to re-decide at the next break in the singing (issue #3265).
      noteSongReloadDeferred();
    }
  }

  // Rebuild the audio player for the song already loaded - used to retry a failed load (item B6).
  // Never while playing: a rebuild tears the current playback down.
  reloadCurrentSong() {
    if (this._playing) {
      return;
    }
    this._audioPlayer = this.createAudioPlayer();
    this.setLoop();
  }

  play() {
    if (!this._playlist) {
      return;
    }

    // Never audibly play a music-copyrighted song, even when playback carries over from the
    // previous song (auto-play, repeat song list, or manual next/previous while playing)
    if (this.currentSongHasMusicCopyrightIssue) {
      this._pause();
      return;
    }

    this._audioPlayer?.play();
    this.markPlaying();
  }

  // the playing flag, the playback store and the stopwatch, for play() and a play on YouTube's own controls
  private markPlaying() {
    this._playing = true;
    this._setPlaying(this._playing);
    if (this._stopwatchSubscription) {
      console.log('Warning: Stopwatch should not be running prior to playing song (iOS browser bug?)');
      this.stopwatch?.unsubscribe(this._stopwatchSubscription);
    }
    this._stopwatchSubscription = this.stopwatch?.subscribe();
  }

  private markPaused() {
    this._playing = false;
    this._setPlaying(this._playing);
    if (this._stopwatchSubscription) {
      this.stopwatch?.unsubscribe(this._stopwatchSubscription);
      this._stopwatchSubscription = undefined;
    }
  }

  // A play or pause on the practice video's own controls: the sequencer follows it without telling the
  // player again, which is what reported it
  private _onExternalPlayingChange(playing: boolean) {
    if (playing === this._playing) {
      return;
    }
    if (playing) {
      this.markPlaying();
    } else {
      this.markPaused();
    }
  }

  get isPlaying() {
    return this._playing;
  }

  pause() {
    this._pause();
  }

  playFirstNote(voiceName: Voice): void {
    // During the introduction the position is parked near the end of the song, but singers
    // start singing at the top, so always preview the song's first note
    const startTick = this.isOnIntroduction ? 0 : this.getNearestMeasureStartTick();
    this._audioPlayer?.playFirstNote(voiceName, startTick);
  }

  private get isOnIntroduction(): boolean {
    return this._verseIndex === 0 && (this.songIntroductionPercent ?? 0) > 0;
  }

  private getNearestMeasureStartTick(): number | undefined {
    const rawPercent = this.getCurrentPercent();
    const ticksPerBeat = this._audioPlayer?.ticksPerBeat;
    const percentages = this.measurePercentagesWithRepeats;
    if (rawPercent === undefined || !ticksPerBeat || this.measureVisitMeasures.length === 0 || !percentages) {
      return;
    }
    // Round to match the precision of measurePercentages (2 decimal places) to avoid
    // floating-point drift from audio currentTime round-trip causing off-by-one measure lookup
    const percent = roundMeasurePercent(rawPercent);
    const { measureIndex, measurePercent } = getMeasureFromPercent(percent, percentages);
    if (measureIndex === undefined || measurePercent === undefined) {
      return;
    }
    // Snap to the nearest measure boundary: if past the midpoint of the current measure,
    // use the next measure; if in the last measure, wrap to the first measure
    const isLastMeasure = measureIndex >= percentages.length - 1;
    if (isLastMeasure) {
      const midpoint = (measurePercent + 100) / 2;
      if (percent >= midpoint) {
        return getMeasureStartTick(0, this.measureVisitMeasures, ticksPerBeat);
      }
    } else {
      const nextMeasurePercent = percentages[measureIndex + 1];
      const midpoint = (measurePercent + nextMeasurePercent) / 2;
      if (percent >= midpoint) {
        return getMeasureStartTick(measureIndex + 1, this.measureVisitMeasures, ticksPerBeat);
      }
    }
    return getMeasureStartTick(measureIndex, this.measureVisitMeasures, ticksPerBeat);
  }

  private get voiceValues(): VoiceValues {
    return { ...(this.voiceValuesOverride ?? this.userVoiceValues), ...this.liveVoiceValues };
  }

  setShowVocalsByDefault(value: boolean) {
    if (value === this.showVocalsByDefault) {
      return;
    }
    this._setReadyToPlay(false);
    this.showVocalsByDefault = value;
    this._audioPlayer = this.createAudioPlayer();
  }

  // A change of path rebuilds the player for the current song, as a change of the vocals default
  // does; the song starts over, stopped
  setWebAudioPlayback(value: boolean) {
    if (value === this.webAudioPlayback) {
      return;
    }
    this.webAudioPlayback = value;
    if (!this._audioPlayer) {
      return;
    }
    this._setReadyToPlay(false);
    this._audioPlayer = this.createAudioPlayer();
  }

  // Switching between practice videos keeps the YouTube player and, for videos of the same length, the place;
  // turning video mode on or off rebuilds the player, as the vocals toggle does
  setPracticeVideo(videoId: YouTubeVideoId | undefined) {
    if (videoId === this.practiceVideoId) {
      return;
    }
    this.practiceVideoId = videoId;
    if (videoId && this._audioPlayer instanceof YouTubeVideoPlayer) {
      this._audioPlayer.setVideoId(videoId);
      return;
    }
    this._setReadyToPlay(false);
    this._audioPlayer = this.createAudioPlayer();
  }

  setPracticeVideoPlaybackRate(rate: number) {
    if (rate === this.practiceVideoPlaybackRate) {
      return;
    }
    this.practiceVideoPlaybackRate = rate;
    if (this._audioPlayer instanceof YouTubeVideoPlayer) {
      this._audioPlayer.setPlaybackRate(rate);
    }
  }

  get videoEnabled(): boolean {
    return !!this.practiceVideoId;
  }

  // The element this sequencer's practice video iframe lives in, made on first use (GitHub issue #4041). It
  // outlives the video players: each renders into a child of it, and the player's video area appends it into its
  // box, so the sequencer builds and destroys players without React knowing about them. One per sequencer, so two
  // players on a page (the stacked Storybook frames) each show their own video.
  get practiceVideoHost(): HTMLDivElement {
    if (!this._practiceVideoHost) {
      const host = document.createElement('div');
      host.style.display = 'block';
      host.style.width = '100%';
      host.style.height = '100%';
      this._practiceVideoHost = host;
    }
    return this._practiceVideoHost;
  }

  get vocalsEnabled(): boolean {
    return !this.videoEnabled && this.songVocalsEnabled;
  }

  // whether the song plays its vocals rather than its piano, outside video mode
  get songVocalsEnabled(): boolean {
    const hasVocals = this._hymn !== undefined && hymnHasVocals(this._hymn);
    const hasMidi = this._hymn !== undefined && !isMidiFileMissing(this._hymn);
    return hasVocals && (this.showVocalsByDefault || !hasMidi);
  }

  private get songIntroductionPercent() {
    if (
      this.vocalsEnabled ||
      this.videoEnabled ||
      !this.songIntroduction ||
      this.songIntroduction === SongIntroduction.FullSong ||
      !this.duration
    ) {
      return 0;
    }
    let desiredIntroDuration: number;
    switch (this.songIntroduction) {
      case SongIntroduction.LastFiveSeconds:
        desiredIntroDuration = 5;
        break;
      case SongIntroduction.LastTenSeconds:
        desiredIntroDuration = 10;
        break;
      case SongIntroduction.LastFifteenSeconds:
        desiredIntroDuration = 15;
        break;
      default:
        ensureUnreachable(this.songIntroduction);
    }
    const desiredPercentage = (100 * Math.max(0, this.duration - desiredIntroDuration)) / this.duration;
    return getMeasureFromPercent(desiredPercentage, this.measurePercentagesWithRepeats).measurePercent;
  }

  private refreshSongIntroduction({ evenIfPlaying }: { evenIfPlaying?: boolean } = {}) {
    if (
      this._verseIndex === 0 &&
      (!this._audioPlayer?.playing || evenIfPlaying) &&
      this._audioPlayer?.duration !== undefined
    ) {
      const songIntroductionPercent = this.songIntroductionPercent;
      if (songIntroductionPercent !== undefined) {
        this._audioPlayer?.setLocationPercentage(songIntroductionPercent);
        this.updateLocationSlider();
      }
    }
  }

  private _pause() {
    this._audioPlayer?.pause();
    this.markPaused();
  }

  // Both MIDI players run straight on into the next verse by themselves (the audio element's loop,
  // the scheduler's); the vocals player and a practice video are restarted for each verse
  private get _supportsLooping() {
    return this._audioPlayer instanceof MidiFilePlayer || this._audioPlayer instanceof MidiFilePlayerWebAudio;
  }

  nextSong() {
    const length = this._playlist?.songs.length ?? 0;
    this.advanceToSong((this._playlistIndex + 1) % length);
  }

  private advanceToSong(newPlaylistIndex: number) {
    if (newPlaylistIndex === 0 && this.repeatMode !== RepeatMode.SongList) {
      this._playing = false;
      this._setPlaying(this._playing);
    }
    this._audioPlayer?.restart();
    this.clearUserSpecifiedTempo?.();
    this.setPlaylistIndex?.(newPlaylistIndex); // will destroy / recreate midi player
  }

  // Called when a song finishes and playback should continue (auto-play or repeat song list).
  // Unlike a manual nextSong, skips songs that cannot be audibly played (music copyright issues),
  // since landing on one would leave the user with silent, uncontrollable playback.
  private autoPlayNextSong() {
    const songs = this._playlist?.songs ?? [];
    for (let step = 1; step <= songs.length; step++) {
      const candidateIndex = (this._playlistIndex + step) % songs.length;
      if (candidateIndex === 0 && this.repeatMode !== RepeatMode.SongList) {
        // wrapped past the last song without repeat: land on the first song, stopped
        // (advanceToSong stops playback, so a copyrighted first song stays silent)
        this.advanceToSong(candidateIndex);
        return;
      }
      const entry = songs[candidateIndex];
      if (!isBound(entry) || !this.songHasMusicCopyrightIssue(entry.slug)) {
        this.advanceToSong(candidateIndex);
        return;
      }
    }
    // every song in the list is music-copyrighted; stop rather than searching forever
    this.reset();
  }

  previousSong() {
    const length = this._playlist?.songs.length ?? 0;
    const playing = this._playing;
    this._pause();
    const newPlaylistIndex = (this._playlistIndex - 1 + length) % length;
    if (newPlaylistIndex !== 0) {
      this._playing = playing;
    }
    this.setPlaylistIndex?.(newPlaylistIndex); // will destroy / recreate midi player
  }

  get initialTime() {
    return this._audioPlayer?.initialTime;
  }

  reset() {
    this.pause();
    this.songIntroduction = localStorageGet(LocalStorageKey.SongIntroduction);
    this.changeVerse(0);
    this._audioPlayer?.restart();
  }

  setNewTime(value: number) {
    this._audioPlayer?.setNewTime(value);
  }

  onVolumeChange() {
    this._audioPlayer?.onVolumeChange();
  }

  getCurrentTempo(): number | undefined {
    return this.currentTempo;
  }

  setUserSpecifiedTempo(centerShift: number, minorAdjustment: number) {
    this.tempoCenterShift = centerShift;
    this.tempoMinorAdjustment = minorAdjustment;
  }

  onTempoChange() {
    this._audioPlayer?.onTempoChange();
  }

  setOrgTempos(orgTempos: SongTempos) {
    const tempoBefore = this.orgTempo;
    this.orgTempos = orgTempos;
    if (tempoBefore === this.orgTempo) {
      return;
    }
    this._audioPlayer?.onTempoChange();
  }

  get orgTempo(): number | undefined {
    if (!this._hymn) {
      return;
    }
    return this.orgTempos[this._hymn.slug];
  }

  get defaultTempo(): number {
    return this.orgTempo ?? this._audioPlayer?.fileTempo ?? getDefaultTempo();
  }

  setOrgTranspositions(orgTranspositions: SongTranspositions) {
    const orgTransposition = this.orgTransposition;
    this.orgTranspositions = orgTranspositions;
    if (orgTransposition === this.orgTransposition) {
      return;
    }
    this._audioPlayer?.onVolumeChange();
  }

  get orgTransposition(): number | undefined {
    if (!this._hymn) {
      return;
    }
    return this.orgTranspositions[this._hymn.slug];
  }

  setUserKeyTransposition(userKeyTransposition: number) {
    if (userKeyTransposition === this.userKeyTransposition) {
      return;
    }
    this.userKeyTransposition = userKeyTransposition;
    this._audioPlayer?.onVolumeChange();
  }

  private get _modulationHalfSteps(): number {
    return (this.userKeyTransposition + (this.orgTransposition ?? 0)) * 2;
  }

  get duration(): number | undefined {
    // in seconds
    return this._audioPlayer?.duration;
  }

  getCurrentPercent(): number | undefined {
    return this._audioPlayer?.getCurrentPercent();
  }

  private setSongReady() {
    this.refreshSongIntroduction();
    this._setReadyToPlay?.(true);
    this._onVerseChange?.(this._verseIndex);
    this.setLoop();
    if (this._playing || this.playOnSongLoad) {
      this.setPlayOnSongLoad(false);
      setTimeout(() => this.play(), 200);
    }
  }

  private setLoop() {
    this._audioPlayer?.setLoop(this._verseIndex !== this.verseCount - 1 && this.verseCount > 1);
  }

  private _handleNetworkError(networkError: NetworkError) {
    // The load failed, so nothing is playing (and an auto-advance into a song that cannot load
    // must not leave the app looking like it is still playing). Saying so also arms the retry,
    // which refuses to rebuild the player while playback is under way (issue #3517 item B6).
    this._playing = false;
    this._setPlaying(this._playing);
    // ... but not a stop: nothing was heard, so the update gate stays open and the hash change
    // that brings the missing files reloads the song at once (issue #3265)
    noteLoadFailed();
    this._onNetworkError?.(networkError);
  }

  private createAudioPlayer() {
    this._clearNetworkError();
    this._midiMissingForSlug = undefined;
    // Whatever asked for the rebuild, the player is now built from this hymn's files: recording
    // the version here is what makes a later hash change - including one deferred while playing -
    // compare against what is actually loaded (issue #3265).
    this._loadedSongVersion = this._hymn && { slug: this._hymn.slug, hash: this._hymn.hash };
    const playing = this._playing;
    if (this._audioPlayer) {
      this._pause();
      this._audioPlayer?.destroy();
      this._audioPlayer = undefined;
    }
    this._playing = playing;

    const hymn = this._hymn;
    if (!hymn) {
      return;
    }

    this._verseIndex = 0;

    let audioPlayer: AudioPlayer;
    if (this.practiceVideoId) {
      audioPlayer = new YouTubeVideoPlayer({
        videoId: this.practiceVideoId,
        host: this.practiceVideoHost,
        loadApi: this.loadYouTubeApi,
        playbackRate: this.practiceVideoPlaybackRate,
        songReady: this.setSongReady,
        onDurationChange: (duration) => this.onDurationChange?.(duration),
        onNetworkError: this._handleNetworkError,
        endOfFile: this._endOfVerse,
        onExternalPlayingChange: this._onExternalPlayingChange,
        updateLocationSlider: this.updateLocationSlider,
        onVideoDataChange: notePracticeVideoData,
      });
    } else if (this.vocalsEnabled) {
      if (!hymn.vocals && !hymn.extensions?.some((extension) => vocalFileExtensions.includes(extension))) {
        return; // not ready to create player yet - will happen in the future
      }
      audioPlayer = new VocalsPlayer({
        dualAudioElement: this.dualAudioElement,
        hymn,
        songReady: this.setSongReady,
        onDurationChange: this.onDurationChange,
        onNetworkError: this._handleNetworkError,
        onVoiceNamesChange: this.onVoiceChange,
        endOfFile: this._endOfVerse,
        getVoiceValues: () => this.voiceValues,
        updateLocationSlider: this.updateLocationSlider,
      });
      // use VocalsPlayerSimple outside the iOS app
      // midiFilePlayer = new VocalsPlayerSimple(
      //   this._hymn.hymnal,
      //   songFilename,
      //   this._songReady,
      //   this._handleNetworkError,
      //   this._endOfVerse,
      // );
    } else if (isSongFileMissing(hymn, SongFileExtension.Mid)) {
      // The manifest says this song has no .mid file - a song whose XML has landed but whose
      // derived files are not generated yet, or a PDF-only custom song. Report what the 404 would
      // have reported, without making the request (issue #3265). Once the files are generated the
      // manifest's hash changes, the song reloads here and the player is built as usual.
      this._midiMissingForSlug = hymn.slug;
      this._handleNetworkError(NetworkError.MidiDownloadFailed);
      return;
    } else if (this.webAudioPlayback) {
      audioPlayer = new MidiFilePlayerWebAudio({
        midiFilePath: getMidiUrlForHymn(hymn),
        setSongReady: this.setSongReady,
        onDurationChange: this.onDurationChange,
        onNetworkError: this._handleNetworkError,
        onVoiceChange: this.onVoiceChange,
        endOfFile: this._endOfVerse,
        modulationHalfSteps: () => this._modulationHalfSteps,
        getCurrentTempo: ensureExists(this.getCurrentTempo),
        updateLocationSlider: this.updateLocationSlider,
        getVoiceValues: () => this.voiceValues,
      });
    } else {
      audioPlayer = new MidiFilePlayer({
        dualAudioElement: this.dualAudioElement,
        midiFilePath: getMidiUrlForHymn(hymn),
        setSongReady: this.setSongReady,
        onDurationChange: this.onDurationChange,
        onNetworkError: this._handleNetworkError,
        onVoiceChange: this.onVoiceChange,
        endOfFile: this._endOfVerse,
        modulationHalfSteps: () => this._modulationHalfSteps,
        getCurrentTempo: ensureExists(this.getCurrentTempo),
        updateLocationSlider: this.updateLocationSlider,
        getVoiceValues: () => this.voiceValues,
      });
    }
    return audioPlayer;
  }

  // Note: introduction counts as a verse
  private get verseCount(): number {
    if (!this._hymn) {
      return 0;
    }
    let verseCount = localStorageGet(LocalStorageKey.VerseCount) || this._hymn.verseCount || defaultVerseCount;
    if (this.songIntroduction) {
      verseCount = verseCount + 1;
    }
    return verseCount;
  }

  private _endOfVerse() {
    this._verseIndex =
      this.vocalsEnabled || this.videoEnabled || (this.playSongOnce && (!this.songIntroduction || this._verseIndex > 0))
        ? 0
        : (this._verseIndex + 1) % this.verseCount;
    this._onVerseChange?.(this._verseIndex);

    if (this._supportsLooping) {
      // the audio element continues onto the next verse automatically, so just refresh the time slider
      this.updateLocationSlider();
      this.setLoop();
    } else {
      this._audioPlayer?.restart();
    }

    if (this._verseIndex === 0) {
      if (this.repeatMode === RepeatMode.Song) {
        this.changeVerse(0);
        return;
      }
      if (this.repeatMode === RepeatMode.SongList || this.isAutoPlayNextSongEnabled) {
        this.autoPlayNextSong();
      } else {
        this.reset();
      }
    } else if (!this._supportsLooping) {
      this._audioPlayer?.play();
    }

    this.refreshSongIntroduction();
  }

  changeVerse(verse: number) {
    this._verseIndex = verse;
    this._onVerseChange?.(this._verseIndex);
    this.setLoop();
    setTimeout(() => {
      this._audioPlayer?.restart();
      this.refreshSongIntroduction({ evenIfPlaying: true });
      if (this._playing) {
        this.play();
      }
    });
  }
}

// The fall-back rules for songs without Syp file voice guidance are intentionally maintained for
// maximum compatibility with arbitrary MIDI files, such as author-provided or
// notation-software-exported files (e.g. the Tanglewood Hymnal LilyPond exports).
function getVoiceName({
  voice,
  sypFile,
  vocalPartCount,
  vocalsEnabled,
}: {
  voice: Voice;
  sypFile: SypMusicFileNew | undefined;
  vocalPartCount: number | undefined;
  vocalsEnabled: boolean | undefined;
}): Voice | null {
  if (vocalsEnabled) {
    return voice;
  }
  const sypFileVoices = (sypFile?.measures[0] as SypMeasureNew)?.staves?.map(({ voice }) => voice);
  if (!sypFileVoices) {
    // no guidance from Syp file -- fall back on old rules
    if (voice === Voice.Piano) {
      return null;
    }
    // only show soprano and tenor if two parts
    if (voice !== Voice.Soprano && voice !== Voice.Tenor && vocalPartCount === 2) {
      return null;
    }
  } else {
    const isUnisonVoice =
      sypFileVoices.length === 1 && (sypFileVoices[0] as unknown as SypMusicFileKey) === SypMusicFileKey.Unison;
    if (!sypFileVoices.includes(voice) && (!isUnisonVoice || voice !== Voice.Soprano)) {
      return null;
    }
  }
  return voice;
}
